import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { FormattedMessage } from 'react-intl';

import CurrencyConverter from '../../CurrencyConverter';
import Link from '../../Link/Link';
import messages from '../../../locale/messages';

import s from './AdminDashboard.css';

const GREY = '#5B6472';
const FONT = "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const StatCard = ({ color, title, data, isCurrency, currency }) => {
  const card = {
    flex: '1 1 200px', minWidth: 180, background: '#fff', border: '1px solid #eee',
    borderTop: `3px solid ${color}`, borderRadius: 14, padding: '16px 18px', fontFamily: FONT,
  };
  const val = (v) => (isCurrency ? <CurrencyConverter from={currency} amount={v} /> : v);
  const sub = (labelKey, v) => (
    <span style={{ marginRight: 12 }}>
      <FormattedMessage {...messages[labelKey]} /> <b style={{ color: '#333' }}>{val(v)}</b>
    </span>
  );
  return (
    <div style={card}>
      <div style={{ fontSize: 12, fontWeight: 600, color: GREY, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        <FormattedMessage {...title} />
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, margin: '4px 0 6px', lineHeight: 1.1 }}>
        {val(data.overall)}
      </div>
      <div style={{ fontSize: 11, color: GREY, display: 'flex', flexWrap: 'wrap' }}>
        {sub('today', data.today)}
        {sub('weekly', data.weekly)}
        {sub('montly', data.montly)}
      </div>
    </div>
  );
};

StatCard.propTypes = {
  color: PropTypes.string,
  title: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  isCurrency: PropTypes.bool,
  currency: PropTypes.string,
};

const AdminDashboard = ({ getDashboardCount, todayEarnings, weeklyEarnings, monthlyEarnings, totalEarnings }) => {
  const {
    totalPartnersCount = 0, todayPartnersCount = 0, weekPartnersCount = 0, monthPartnersCount = 0,
    totalUsersCount = 0, todayUsersCount = 0, weekUsersCount = 0, monthUsersCount = 0,
    totalBookingsCount = 0, todayBookingsCount = 0, weekBookingsCount = 0, monthBookingsCount = 0,
    currency, deferredBackgroundCheckCount = 0, freeBackgroundCheckCount = 0, availableJobsCount = 0,
    activeBookingsCount = 0,
  } = getDashboardCount?.result || {};

  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', padding: '8px 0 4px', fontFamily: FONT }}>
      {/* Live open-bookings gauge (watch-tower): +1 on every new booking, -1 when one completes or
          cancels. Refreshes with the dashboard's 20s poll. Clicking opens the full jobs list. */}
      <Link to="/siteadmin/jobs" style={{ textDecoration: 'none', flex: '1 1 200px', minWidth: 180 }}>
        <div style={{
          background: '#fff', border: '1px solid #eee', borderTop: '3px solid #16A34A',
          borderRadius: 14, padding: '16px 18px', fontFamily: FONT, cursor: 'pointer', height: '100%',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: GREY, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Active bookings
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#16A34A', margin: '4px 0 6px', lineHeight: 1.1 }}>
            {activeBookingsCount}
          </div>
          <div style={{ fontSize: 11, color: GREY }}>
            Open right now — up on new, down on completed
          </div>
        </div>
      </Link>
      <StatCard
        color="#0E9488"
        title={messages.users}
        data={{ today: todayUsersCount, weekly: weekUsersCount, montly: monthUsersCount, overall: totalUsersCount }}
      />
      <StatCard
        color="#1565C0"
        title={messages.partners}
        data={{ today: todayPartnersCount, weekly: weekPartnersCount, montly: monthPartnersCount, overall: totalPartnersCount }}
      />
      <StatCard
        color="#1F2733"
        title={messages.bookings}
        data={{ today: todayBookingsCount, weekly: weekBookingsCount, montly: monthBookingsCount, overall: totalBookingsCount }}
      />
      <StatCard
        color="#D32F2F"
        title={messages.totalEarnings}
        isCurrency
        currency={currency}
        data={{ today: todayEarnings, weekly: weeklyEarnings, montly: monthlyEarnings, overall: totalEarnings }}
      />
      {/* Deferred background check requests awaiting validation — clicking opens the section to validate them. */}
      <Link to="/siteadmin/deferred-background-checks" style={{ textDecoration: 'none', flex: '1 1 200px', minWidth: 180 }}>
        <div style={{
          background: '#fff', border: '1px solid #eee', borderTop: '3px solid #F59E0B',
          borderRadius: 14, padding: '16px 18px', fontFamily: FONT, cursor: 'pointer', height: '100%',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: GREY, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            <FormattedMessage {...messages.deferredBackgroundChecks} />
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#F59E0B', margin: '4px 0 6px', lineHeight: 1.1 }}>
            {deferredBackgroundCheckCount}
          </div>
          <div style={{ fontSize: 11, color: GREY }}>
            <FormattedMessage {...messages.deferredAwaitingValidation} />
          </div>
        </div>
      </Link>
      {/* Free / protected-state background checks — acknowledging is non-blocking, it just clears this badge. */}
      <Link to="/siteadmin/free-background-checks" style={{ textDecoration: 'none', flex: '1 1 200px', minWidth: 180 }}>
        <div style={{
          background: '#fff', border: '1px solid #eee', borderTop: '3px solid #0E9488',
          borderRadius: 14, padding: '16px 18px', fontFamily: FONT, cursor: 'pointer', height: '100%',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: GREY, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            <FormattedMessage {...messages.freeBackgroundChecks} />
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#0E9488', margin: '4px 0 6px', lineHeight: 1.1 }}>
            {freeBackgroundCheckCount}
          </div>
          <div style={{ fontSize: 11, color: GREY }}>
            <FormattedMessage {...messages.deferredAwaitingValidation} />
          </div>
        </div>
      </Link>
      {/* COMPLIANCE / FCRA adverse-action sequence (PRE_ADVERSE -> reasonable-wait -> FINAL). Action-driven
          by worker userId rather than a badge count, so no number here. */}
      <Link to="/siteadmin/adverse-action" style={{ textDecoration: 'none', flex: '1 1 200px', minWidth: 180 }}>
        <div style={{
          background: '#fff', border: '1px solid #eee', borderTop: '3px solid #B91C1C',
          borderRadius: 14, padding: '16px 18px', fontFamily: FONT, cursor: 'pointer', height: '100%',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: GREY, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            <FormattedMessage {...messages.adverseActionTitle} />
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#B91C1C', margin: '4px 0 6px', lineHeight: 1.1 }}>
            &nbsp;
          </div>
          <div style={{ fontSize: 11, color: GREY }}>
            <FormattedMessage {...messages.adverseActionCardSubtitle} />
          </div>
        </div>
      </Link>
      {/* Active broadcast "Available Jobs" not yet claimed — live count (auto up on broadcast, down on claim).
          Clicking opens the list to monitor jobs and alert workers. */}
      <Link to="/siteadmin/available-jobs" style={{ textDecoration: 'none', flex: '1 1 200px', minWidth: 180 }}>
        <div style={{
          background: '#fff', border: '1px solid #eee', borderTop: '3px solid #DC2626',
          borderRadius: 14, padding: '16px 18px', fontFamily: FONT, cursor: 'pointer', height: '100%',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: GREY, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            <FormattedMessage {...messages.availableJobs} />
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#DC2626', margin: '4px 0 6px', lineHeight: 1.1 }}>
            {availableJobsCount}
          </div>
          <div style={{ fontSize: 11, color: GREY }}>
            <FormattedMessage {...messages.availableJobsWaiting} />
          </div>
        </div>
      </Link>
    </div>
  );
};

AdminDashboard.propTypes = {
  getDashboardCount: PropTypes.object,
  todayEarnings: PropTypes.number,
  weeklyEarnings: PropTypes.number,
  monthlyEarnings: PropTypes.number,
  totalEarnings: PropTypes.number,
};

export default withStyles(s)(AdminDashboard);
